import { array, mixed, number, object, string } from "yup";

const hasValue = (obj, keys) =>
  keys.some(
    key =>
      Object.prototype.hasOwnProperty.call(obj || {}, key) &&
      obj[key] !== null &&
      obj[key] !== ""
  );

const requiredId = label =>
  mixed()
    .required(`${label} is required`)
    .test(`${label}:type`, `${label} must be a string or number`, value => {
      return typeof value === "string" || typeof value === "number";
    });

const requiredObject = label =>
  mixed()
    .defined(`${label} is required`)
    .test({
      name: `${label}:is-object`,
      test(value) {
        return value && typeof value === "object" && !Array.isArray(value)
          ? true
          : this.createError({ message: `${label} must be an object` });
      },
    });

const makeResult = ({ ok, type, payload, err }) =>
  ok
    ? { valid: true, error: null, details: [], actionType: type, payload }
    : {
        valid: false,
        error: `${type}: ${
          Array.isArray(err?.errors)
            ? err.errors.join(" | ")
            : String(err?.message || "Invalid payload")
        }`,
        details: Array.isArray(err?.inner)
          ? err.inner.map(e => ({ path: e.path, message: e.message }))
          : [],
        actionType: type,
        payload,
      };

const SET_STATE_Payload = array()
  .required("Payload is required")
  .typeError("Payload must be an array");

const ADD_NEW_INCIDENT_Payload = object()
  .required("Payload is required")
  .typeError("Payload must be an object")
  .test({
    name: "add-new-incident:shape",
    test(value) {
      return value && typeof value === "object" && !Array.isArray(value)
        ? true
        : this.createError({ message: "Payload must be an object" });
    },
  });

const REMOVE_ACTIVE_INCIDENT_Payload = requiredId("id");

const ADD_OIS_TO_INCIDENT_Payload = object({
  incidentIndex: number()
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .nullable(true),
  incidentId: requiredId("incidentId"),
  ois: requiredObject("ois"),
})
  .required("Payload is required")
  .typeError("Payload must be an object");

const UPDATE_OIS_Payload = object({
  incidentIndex: number()
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .nullable(true),
  incidentId: mixed().nullable(true),
  oisIndex: number()
    .integer("oisIndex must be an integer")
    .min(0, "oisIndex must be >= 0")
    .nullable(true),
  oisId: mixed().nullable(true),
  property: string().required("property is required"),
  value: mixed().defined("value is required"),
})
  .required("Payload is required")
  .typeError("Payload must be an object")
  .test({
    name: "update-ois:addressing",
    test(value) {
      const v = value || {};
      const hasIncident = hasValue(v, ["incidentId", "incidentIndex"]);
      const hasOis = hasValue(v, ["oisId", "oisIndex"]);
      if (!hasIncident) {
        return this.createError({
          message: "Provide incidentId or incidentIndex",
        });
      }
      if (!hasOis) {
        return this.createError({ message: "Provide oisId or oisIndex" });
      }
      return true;
    },
  });

const UPDATE_DEPT_ATTRB_Payload = object({
  incidentId: requiredId("incidentId"),
  oisId: requiredId("oisId"),
  property: string().required("property is required"),
  value: mixed().defined("value is required"),
})
  .required("Payload is required")
  .typeError("Payload must be an object");

const ADD_OR_UPDATE_ACTIVE_INCIDENT_Payload = requiredObject("Payload");

const REPLACE_ACTIVE_INCIDENT_Payload = object()
  .required("Payload is required")
  .typeError("Payload must be an object")
  .test({
    name: "replace-active-incident:addressing",
    test(value) {
      return hasValue(value || {}, ["id", "uuid"])
        ? true
        : this.createError({ message: "Provide id or uuid" });
    },
  });

const UPDATE_ACTIVE_INCIDENT_PROPERTY_Payload = object({
  incidentId: requiredId("incidentId"),
  accessor: string().nullable(true),
  property: string().required("property is required"),
  value: mixed().nullable(true).defined("value is required"),
  nextProperty: string().nullable(true),
  nextValue: mixed().nullable(true),
})
  .required("Payload is required")
  .typeError("Payload must be an object");

const REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE_Payload = object({
  incidentIndex: number()
    .integer("incidentIndex must be an integer")
    .min(0, "incidentIndex must be >= 0")
    .nullable(true),
  incidentId: mixed().nullable(true),
  oisIndex: number()
    .integer("oisIndex must be an integer")
    .min(0, "oisIndex must be >= 0")
    .nullable(true),
  oisId: mixed().nullable(true),
})
  .required("Payload is required")
  .typeError("Payload must be an object")
  .test({
    name: "remove-ois:addressing",
    test(value) {
      const v = value || {};
      const hasIncident = hasValue(v, ["incidentId", "incidentIndex"]);
      const hasOis = hasValue(v, ["oisId", "oisIndex"]);
      if (!hasIncident) {
        return this.createError({
          message: "Provide incidentId or incidentIndex",
        });
      }
      if (!hasOis) {
        return this.createError({ message: "Provide oisId or oisIndex" });
      }
      return true;
    },
  });

const UPDATE_OIS_DASH_MULTI_FIELD_Payload = object({
  incidentId: requiredId("incidentId"),
  oisId: mixed().nullable(true),
  oisUuid: mixed().nullable(true),
  updatedOis: requiredObject("updatedOis"),
})
  .required("Payload is required")
  .typeError("Payload must be an object")
  .test({
    name: "update-ois-dash-multi-field:addressing",
    test(value) {
      return hasValue(value || {}, ["oisId", "oisUuid"])
        ? true
        : this.createError({ message: "Provide oisId or oisUuid" });
    },
  });

const UPDATE_AVAILABLE_OFFICER_Payload = object({
  officerId: requiredId("officerId"),
  property: string().required("property is required"),
  value: mixed().defined("value is required"),
})
  .required("Payload is required")
  .typeError("Payload must be an object");

const payloadSchemas = {
  "set-state": SET_STATE_Payload,
  "add-new-incident": ADD_NEW_INCIDENT_Payload,
  "remove-active-incidents": REMOVE_ACTIVE_INCIDENT_Payload,
  "add-ois-to-incident": ADD_OIS_TO_INCIDENT_Payload,
  "update-ois": UPDATE_OIS_Payload,
  "update-dept-attrb": UPDATE_DEPT_ATTRB_Payload,
  "add-or-up-active-incident": ADD_OR_UPDATE_ACTIVE_INCIDENT_Payload,
  "replace-active-incident": REPLACE_ACTIVE_INCIDENT_Payload,
  "update-active-incident-property": UPDATE_ACTIVE_INCIDENT_PROPERTY_Payload,
  "remove-ois-from-active-incident-table":
    REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE_Payload,
  "update-ois-dash-multi-field": UPDATE_OIS_DASH_MULTI_FIELD_Payload,
  "update-available-officer": UPDATE_AVAILABLE_OFFICER_Payload,
};

export function dashboardReducerValidator(type, payload) {
  const schema = payloadSchemas[type];
  if (!schema) {
    return {
      valid: false,
      error: "Case not found. Please update the validation.",
      details: [],
      actionType: type,
      payload,
    };
  }

  try {
    schema.validateSync(payload, {
      abortEarly: false,
      stripUnknown: false,
      strict: true,
    });
    return makeResult({ ok: true, type, payload });
  } catch (err) {
    return makeResult({ ok: false, type, payload, err });
  }
}
